import { Button, Form, Radio, Space, Switch, Typography, type InputRef } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem, Input as ZhInput } from "zhihao-ui-react";

import request from "@/api";
import { personService } from "@/api/services/personService";
import type { TreePerson, TreePersonNode } from "@/entity/person";
import { USER_ORIGIN } from "@/meta/manageCenter/user";
import type { SystemRole } from "../role/types";
import { BackendAccountRoleSelector, loadBackendAccountRoles } from "./BackendAccountRoleSelector";

export interface BackendAccountFormValues {
    id?: number | string;
    origin: number;
    name: string;
    mobiles: string[];
    roleIds: number[];
    state: number;
}

export interface BackendAccountDetail extends Partial<BackendAccountFormValues> {
    id?: number | string;
    roles?: SystemRole[];
    user?: Partial<BackendAccountFormValues> & { roles?: SystemRole[] };
}

export interface BackendAccountFormPageProps {
    loadUser?: (id: number | string) => Promise<BackendAccountDetail>;
    loadRoles?: (query: {
        page: number;
        size: number;
        name: string;
    }) => Promise<{ list?: SystemRole[]; total?: number }>;
    saveUser?: (id: number | string | undefined, values: BackendAccountFormValues) => Promise<void>;
    loadPersonTree?: () => Promise<TreePerson>;
}

/** 复用旧 Vue 后台用户新增页的组织人员树接口。 */
export async function loadBackendAccountPersonTree(): Promise<TreePerson> {
    return personService.getPersonTree();
}

/** 复用旧 Vue `/admin/user/vo/:id` 详情接口。 */
export async function loadBackendAccount(id: number | string): Promise<BackendAccountDetail> {
    const response = await request.service.get<BackendAccountDetail>(`/admin/user/vo/${id}`);
    return response.data;
}

/** 复用旧 Vue 后台用户新增/编辑接口。 */
export async function saveBackendAccount(
    id: number | string | undefined,
    values: BackendAccountFormValues
): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/user", values);
        return;
    }
    await request.service.put(`/admin/user/${id}`, { ...values, id });
}

interface BackendAccountFormState {
    id?: number | string;
    name: string;
    mobilesText: string;
    roleIds: number[];
    origin: number;
    state: boolean;
}

interface PersonTreeSelectorProps {
    value?: number | string;
    onChange?: (value: number | string) => void;
    loadTree: () => Promise<TreePerson>;
}

/** 志豪集团成员选择器，保留旧 Vue 的按组织树搜索员工契约。 */
function PersonTreeSelector({ value, onChange, loadTree }: PersonTreeSelectorProps) {
    const [keyword, setKeyword] = useState("");
    const [tree, setTree] = useState<TreePersonNode[]>([]);
    // 旧 Vue el-tree 默认只展开根组织，子组织由用户点击箭头展开。
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [open, setOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const inputRef = useRef<InputRef>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 旧 Vue el-dropdown 会在点击组件外部时自动收起，Portal 弹层需要自行补齐该行为。
    useEffect(() => {
        if (!open) return;

        const handleOutsidePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;

            // 输入框和挂载到 body 的弹层都属于选择器内部，点击它们时保持打开状态。
            if (inputRef.current?.input?.contains(target) || dropdownRef.current?.contains(target)) return;
            setOpen(false);
        };

        // 使用捕获阶段，保证外部点击在其它页面组件处理前即可关闭悬浮层。
        document.addEventListener("pointerdown", handleOutsidePointerDown, true);
        return () => document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    }, [open]);

    // 将弹框挂到 body，脱离主内容滚动容器的 overflow 裁剪，再随输入框滚动位置更新。
    useLayoutEffect(() => {
        if (!open) {
            setDropdownPosition(null);
            return;
        }

        const updateDropdownPosition = () => {
            const input = inputRef.current?.input;
            if (!input) return;
            const rect = input.getBoundingClientRect();
            setDropdownPosition({ top: rect.bottom + 12, left: rect.left - 208 });
        };

        updateDropdownPosition();
        window.addEventListener("resize", updateDropdownPosition);
        window.addEventListener("scroll", updateDropdownPosition, true);
        return () => {
            window.removeEventListener("resize", updateDropdownPosition);
            window.removeEventListener("scroll", updateDropdownPosition, true);
        };
    }, [open]);

    const load = async () => {
        setOpen(true);
        if (tree.length) return;
        try {
            const result = await loadTree();
            const rootNodes = result.rootNode || [];
            setTree(rootNodes);
            setExpandedIds(new Set(rootNodes.map(node => node.id)));
        } catch {
            setTree([]);
        }
    };
    // 用递归保留旧 Vue el-tree 的部门层级，人员节点仍可直接选择并回填表单。
    const renderNodes = (nodes: TreePersonNode[], depth = 0): ReactNode[] =>
        nodes.flatMap(node => {
            // 旧 Vue 将直属人员和子部门都作为 el-tree 的可展开节点处理。
            const hasChildren = Boolean(node.dataList?.length || node.childNodes?.length);
            const isExpanded = expandedIds.has(node.id);
            return [
                <div
                    key={`dept-${node.id}`}
                    className="backend-account-person-tree__department"
                    style={{ paddingLeft: `${depth * 22 + 10}px` }}
                    onClick={() => {
                        if (!hasChildren) return;
                        setExpandedIds(previous => {
                            const next = new Set(previous);
                            if (next.has(node.id)) next.delete(node.id);
                            else next.add(node.id);
                            return next;
                        });
                    }}
                >
                    <span aria-hidden="true" className="backend-account-person-tree__arrow">
                        {hasChildren ? (isExpanded ? "▾" : "▸") : ""}
                    </span>
                    <span
                        aria-hidden="true"
                        className={`iconfont backend-account-person-tree__folder ${
                            isExpanded ? "icon-a-wenjianjia-kai1" : "icon-a-wenjianjia-guan1"
                        }`}
                    />
                    <span className="backend-account-person-tree__label">{node.data.name}</span>
                </div>,
                ...(isExpanded
                    ? (node.dataList || [])
                          .filter(person => !keyword || person.name.includes(keyword))
                          .map(person => (
                              <button
                                  className="backend-account-person-tree__person"
                                  key={`person-${person.id}`}
                                  style={{ paddingLeft: `${(depth + 1) * 22 + 28}px` }}
                                  type="button"
                                  onClick={() => {
                                      onChange?.(person.id);
                                      setKeyword(person.name);
                                      setOpen(false);
                                  }}
                              >
                                  <span className="backend-account-person-tree__person-title">
                                      <span aria-hidden="true" className="iconfont icon-a-Group2331" />
                                      <span className="backend-account-person-tree__label">{person.name}</span>
                                  </span>
                                  <span
                                      aria-hidden="true"
                                      className={`iconfont backend-account-person-tree__check ${
                                          value === person.id ? "icon-danxuankuangxuanzhong" : "icon-danxuankuang"
                                      }`}
                                  />
                              </button>
                          ))
                    : []),
                ...(isExpanded ? renderNodes(node.childNodes || [], depth + 1) : [])
            ];
        });

    return (
        <div aria-label="后台成员选择器" className="backend-account-person-tree">
            <ZhInput
                aria-label="姓名"
                placeholder="请选择员工"
                ref={inputRef}
                value={keyword}
                onChange={event => {
                    setKeyword(event.target.value);
                    if (!open) void load();
                }}
                onFocus={() => void load()}
            />
            {open && dropdownPosition
                ? createPortal(
                      <div
                          className="backend-account-person-tree__dropdown"
                          ref={dropdownRef}
                          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                      >
                          {renderNodes(tree)}
                      </div>,
                      document.body
                  )
                : null}
            {value != null && !keyword ? <span className="sr-only">已选择员工</span> : null}
        </div>
    );
}

/** 后台用户新增/编辑页，保留 Vue 提交所需的用户、手机号、角色和状态字段。 */
export function BackendAccountFormPage({
    loadUser: load = loadBackendAccount,
    loadRoles = loadBackendAccountRoles,
    saveUser: save = saveBackendAccount,
    loadPersonTree = loadBackendAccountPersonTree
}: BackendAccountFormPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: pathId } = useParams<{ id: string }>();
    const [form] = Form.useForm<BackendAccountFormState>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [editDetails, setEditDetails] = useState<{ name: string; origin?: number }>({ name: "" });
    // 记录提交按钮，区分 Vue 的普通保存与“保存并添加下一个”流程。
    const submitModeRef = useRef<"save" | "next">("save");
    // 兼容旧 Vue 的 query 编辑链接，同时优先使用 React 路由声明的路径参数。
    const id = pathId || new URLSearchParams(location.search).get("id") || undefined;
    const origin = Form.useWatch("origin", form) ?? USER_ORIGIN.ZHIHAO.code;
    const name = Form.useWatch("name", form) || "";
    const isEdit = Boolean(id);
    const displayedOrigin = isEdit ? (editDetails.origin ?? origin) : origin;
    const displayedName = isEdit ? editDetails.name : name;

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(info => {
                if (!disposed) {
                    const source = info.user || info;
                    // Vue 接口的 origin/state 可能以字符串返回，先统一为数字再驱动控件回显。
                    const origin = source.origin == null ? USER_ORIGIN.ZHIHAO.code : Number(source.origin);
                    const state = source.state == null ? 1 : Number(source.state);
                    const roleIds =
                        (source.roleIds
                            ? source.roleIds.map(roleId => Number(roleId)).filter(Number.isFinite)
                            : undefined) ||
                        source.roles?.map(role => Number(role.id)).filter(Number.isFinite) ||
                        info.roles?.map(role => Number(role.id)).filter(Number.isFinite) ||
                        [];
                    setEditDetails({ name: source.name || "", origin });
                    form.setFieldsValue({
                        id: source.id,
                        name: source.name || "",
                        mobilesText: source.mobiles?.join(",") || "",
                        roleIds,
                        origin,
                        state: state !== 0
                    });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("后台用户信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [form, id, load]);

    const handleFinish = async (values: BackendAccountFormState) => {
        const mobiles = (values.mobilesText || "")
            .split(/[，,]/)
            .map(item => item.trim())
            .filter(Boolean);
        const shouldAddNext = submitModeRef.current === "next";
        submitModeRef.current = "save";
        try {
            await save(id, {
                id: values.id,
                origin: values.origin || USER_ORIGIN.ZHIHAO.code,
                name: values.name,
                mobiles,
                roleIds: values.roleIds || [],
                state: values.state ? 1 : 0
            });
            if (!id && shouldAddNext) {
                // Vue 保存下一条时清空已填字段，但保留用户类型供连续录入使用。
                form.resetFields();
                form.setFieldsValue({ origin: values.origin, state: true });
                setErrorMessage(null);
                return;
            }
            void navigate(-1);
        } catch {
            setErrorMessage("后台用户保存失败");
        }
    };

    return (
        <section
            className="backend-account-form-page flex h-full min-h-full flex-col"
            aria-label="backend-account-form-page"
        >
            <Typography.Title className="backend-account-form-title" level={4}>
                {isEdit ? "编辑后台用户" : "新增后台用户"}
            </Typography.Title>
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            <Form<BackendAccountFormState>
                className="backend-account-form"
                form={form}
                initialValues={{ origin: USER_ORIGIN.ZHIHAO.code, state: true }}
                labelAlign="right"
                labelCol={{ flex: "90px" }}
                layout="horizontal"
                onFinish={values => void handleFinish(values)}
            >
                <Form.Item className="backend-account-form-item--full" label="用户类型" name="origin">
                    {isEdit ? (
                        <span className="backend-account-form-readonly">
                            {USER_ORIGIN.getIntroByCode(displayedOrigin)}
                        </span>
                    ) : (
                        <Radio.Group>
                            <Radio value={USER_ORIGIN.ZHIHAO.code}>{USER_ORIGIN.ZHIHAO.intro}</Radio>
                            <Radio value={USER_ORIGIN.ENTERPRISE.code}>{USER_ORIGIN.ENTERPRISE.intro}</Radio>
                        </Radio.Group>
                    )}
                </Form.Item>
                {isEdit ? (
                    <>
                        <Form.Item className="backend-account-form-item--half" label="姓名">
                            <span className="backend-account-form-readonly">{displayedName || "--"}</span>
                        </Form.Item>
                        <Form.Item hidden name="id">
                            <input aria-hidden="true" />
                        </Form.Item>
                        <Form.Item hidden name="name">
                            <input aria-hidden="true" />
                        </Form.Item>
                    </>
                ) : origin === USER_ORIGIN.ZHIHAO.code ? (
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <ZhGridItem span={1}>
                            <Form.Item name="id" label="姓名" rules={[{ required: true, message: "请选择员工" }]}>
                                <PersonTreeSelector loadTree={loadPersonTree} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={3} />
                    </ZhGrid>
                ) : (
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <ZhGridItem span={1}>
                            <Form.Item label="姓名" name="name" rules={[{ required: true, message: "请输入姓名" }]}>
                                <ZhInput placeholder="请输入姓名" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={3} />
                    </ZhGrid>
                )}
                {displayedOrigin === USER_ORIGIN.ENTERPRISE.code ? (
                    <Form.Item
                        label="手机号"
                        name="mobilesText"
                        extra="多个手机号使用逗号分隔"
                        dependencies={["origin"]}
                        rules={[
                            {
                                validator: async (_, value: string) => {
                                    const mobiles = (value || "")
                                        .split(/[，,]/)
                                        .map(item => item.trim())
                                        .filter(Boolean);
                                    if (!mobiles.length) throw new Error("请输入手机号");
                                    if (mobiles.some(item => !/^1[3-9]\d{9}$/.test(item)))
                                        throw new Error("请输入正确的手机号");
                                    if (mobiles.length !== new Set(mobiles).size) throw new Error("手机号不能重复");
                                }
                            }
                        ]}
                    >
                        <ZhInput placeholder="请输入手机号" />
                    </Form.Item>
                ) : null}
                <Form.Item
                    className="backend-account-form-item--half"
                    label="授权角色"
                    name="roleIds"
                    rules={[{ required: true, type: "array", min: 1, message: "请选择授权角色" }]}
                >
                    <BackendAccountRoleSelector editMode={isEdit} loadRoles={loadRoles} />
                </Form.Item>
                <Form.Item
                    className="backend-account-form-item--full"
                    label="状态"
                    name="state"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Space className="operation-fixed-btn" size={0}>
                    <Button
                        htmlType="submit"
                        type="primary"
                        onClick={() => {
                            submitModeRef.current = "save";
                        }}
                    >
                        保存
                    </Button>
                    {!id ? (
                        <Button
                            htmlType="submit"
                            onClick={() => {
                                submitModeRef.current = "next";
                            }}
                        >
                            保存并添加下一个
                        </Button>
                    ) : null}
                    <Button onClick={() => void navigate(-1)}>返回</Button>
                </Space>
            </Form>
        </section>
    );
}

export default BackendAccountFormPage;
